import React from 'react';
import type { CalendarEvent } from '../../hooks/useEvents';
import { useUser } from '../../contextManager/user.context';
import  LessonCard from './LessonCard';
import  CounterpartyCard from './CounterpartyCard';
import { LessonDetailView } from './LessonDetailView';
import { createPortal } from 'react-dom';
import { BackButton, Dialog, List, Overlay, Spacer, ToggleBtn, TopRow, ViewToggle } from '../../styles/lesson/lesson.style';
import { useLessonData } from '../../hooks/useLessonData';
import { useLessonNavigation } from '../../hooks/useLessonNavigation';
import { friendlyTzName } from '../../utils/timezones';


type Mode = 'upcoming' | 'completed';

export const LessonList: React.FC<{
  events?: CalendarEvent[];
  mode: Mode;
}> = ({ events = [], mode }) => {
  const { userDetails } = useUser();
  const userId = userDetails?.id;
  const myTz = userDetails?.timezone || 'Europe/London';

  const { groupedList, sortedList, isTutorViewer } = useLessonData(events, userId, mode);

  // 2. NAV HOOK: Handles interaction, state, and derived UI
  const {
    view,
    selectedCounterparty,
    openEvent,
    setOpenEvent,
    selectedPersonEvents,
    avatarForDetailView,
    setView,
    goBack,
    selectPerson,
    closeModal
  } = useLessonNavigation(groupedList, userId);

  return (
    <>
      <TopRow>
        {view === 'person' && selectedCounterparty && (
          <BackButton onClick={goBack}>← Back</BackButton>
        )}

        <Spacer />

        {!selectedCounterparty && (
          <ViewToggle>
            <ToggleBtn active={view === 'date'} onClick={() => setView('date')}>
              By date
            </ToggleBtn>
            <ToggleBtn active={view === 'person'} onClick={() => setView('person')}>
              By {isTutorViewer ? 'parent' : 'tutor'}
            </ToggleBtn>
          </ViewToggle>
        )}
      </TopRow>

      {(sortedList.length > 0 || groupedList.length > 0) && (
        <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: '0 0 0.75rem', textAlign: 'center' }}>
          Times shown in {friendlyTzName(myTz)}
        </p>
      )}

      {/* VIEW 1: Simple Date List */}
      {view === 'date' && (
        <List>
          {sortedList.length === 0 ? <NoData mode={mode} /> :
            sortedList.map(ev => (
              <LessonCard key={ev.id} event={ev} onOpen={setOpenEvent} />
            ))
          }
        </List>
      )}

      {/* VIEW 2: Grouped List (Top Level) */}
      {view === 'person' && !selectedCounterparty && (
        <List>
          {groupedList.length === 0 ? <NoData mode={mode} /> : 
            groupedList.map(group => (
              <CounterpartyCard
                key={group.id}
                name={group.name}
                isTutor={!isTutorViewer}
                nextEvent={group.next}
                count={group.events.length}
                onOpen={() => selectPerson(group.id)}
                avatar={group.avatar}
              />
            ))
          }
        </List>
      )}

      {/* VIEW 3: Grouped List (Drill Down) */}
      {view === 'person' && selectedCounterparty && (
        <List>
          {selectedPersonEvents.map(ev => (
            <LessonCard key={ev.id} event={ev} onOpen={setOpenEvent} />
          ))}
        </List>
      )}

      {/* MODAL */}
      {openEvent && createPortal(
        <Overlay onClick={closeModal} style={{ zIndex: 9999 }}>
          <Dialog onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', height: '90vh', overflow: 'hidden', padding: 0, background: 'transparent', boxShadow: 'none', display: 'flex' }}>
            <LessonDetailView
              event={openEvent}
              onClose={closeModal}
              isTutor={isTutorViewer}
              currentUserId={userId}
              avatar={avatarForDetailView}
            />
          </Dialog>
        </Overlay>,
        document.body
      )}
    </>
  );
};

const NoData = ({ mode }: { mode: string }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1.5rem',
    textAlign: 'center',
    gap: '1rem',
    width: '100%',
    margin: '0 auto',
  }}>
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {mode === 'upcoming' ? (
        <>
          {/* Calendar with sparkle */}
          <rect x="14" y="18" width="52" height="46" rx="6" fill="var(--colors-blue3, #dbeafe)" stroke="var(--colors-blue7, #93c5fd)" strokeWidth="2"/>
          <rect x="14" y="18" width="52" height="14" rx="6" fill="var(--colors-blue5, #bfdbfe)"/>
          <rect x="14" y="26" width="52" height="6" fill="var(--colors-blue5, #bfdbfe)"/>
          <circle cx="28" cy="24" r="2" fill="var(--colors-blue9, #3b82f6)"/>
          <circle cx="40" cy="24" r="2" fill="var(--colors-blue9, #3b82f6)"/>
          <circle cx="52" cy="24" r="2" fill="var(--colors-blue9, #3b82f6)"/>
          <line x1="24" y1="42" x2="36" y2="42" stroke="var(--colors-sand6)" strokeWidth="2" strokeLinecap="round"/>
          <line x1="24" y1="48" x2="44" y2="48" stroke="var(--colors-sand6)" strokeWidth="2" strokeLinecap="round"/>
          <line x1="24" y1="54" x2="32" y2="54" stroke="var(--colors-sand6)" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="62" cy="16" r="5" fill="var(--colors-blue9, #3b82f6)" opacity="0.3"/>
          <path d="M62 12v8M58 16h8" stroke="var(--colors-blue9, #3b82f6)" strokeWidth="1.5" strokeLinecap="round"/>
        </>
      ) : (
        <>
          {/* Checkmark with confetti */}
          <circle cx="40" cy="40" r="24" fill="var(--colors-green3, #dcfce7)" stroke="var(--colors-green7, #86efac)" strokeWidth="2"/>
          <path d="M28 40l8 8 16-16" stroke="var(--colors-green9, #22c55e)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="18" cy="20" r="3" fill="var(--colors-blue5)" opacity="0.5"/>
          <circle cx="64" cy="22" r="2" fill="var(--colors-amber5)" opacity="0.6"/>
          <circle cx="60" cy="60" r="2.5" fill="var(--colors-blue5)" opacity="0.4"/>
          <circle cx="22" cy="58" r="2" fill="var(--colors-green5)" opacity="0.5"/>
        </>
      )}
    </svg>
    <div>
      <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: '0.95rem', color: 'var(--colors-sand12)' }}>
        {mode === 'upcoming' ? 'No upcoming lessons' : 'No completed lessons yet'}
      </p>
      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--colors-sand10)', lineHeight: 1.4 }}>
        {mode === 'upcoming'
          ? 'Book a lesson to get started — they\'ll appear here.'
          : 'Once you\'ve had your first lesson, it\'ll show up here.'}
      </p>
    </div>
  </div>
);